import React, { useRef, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  useWindowDimensions,
} from "react-native";

import global from "../../../styles/globalStyles";
import { PrimaryButton, NormalText } from "../../common/native/index";
import { Plus, Upload } from "../../../assets";
import {
  ImageUploadManager,
  UploadedImage,
  IMAGE_CATEGORIES,
  ImageUploadState,
} from "../../../utils/imageUpload";
import { TSurgeryStageType } from "../../../utils/types";

/* ------------------------------------------------------------------ */
/*  Props & constants                                                 */
/* ------------------------------------------------------------------ */

interface ImageUploadComponentProps {
  uploadState: ImageUploadState;
  uploadManager: ImageUploadManager;
  onUpload: () => void;
  showUploadButton?: boolean;
  containerStyle?: any;
}

export const VALID_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];
export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

const ImageUploadComponent: React.FC<ImageUploadComponentProps> = ({
  uploadState,
  uploadManager,
  onUpload,
  showUploadButton = true,
  containerStyle,
}) => {
  const { width } = useWindowDimensions();
  const isMobile = width < 644;

  /* ---------------- drag‑state bookkeeping ----------------------- */
  const [dragStates, setDragStates] = useState<
    Record<TSurgeryStageType, boolean>
  >({
    preop: false,
    markings: false,
    postop: false,
    intraop: false,
    wish: false,
  });
  const [errors, setErrors] = useState<Record<TSurgeryStageType, string>>({
    preop: "",
    markings: "",
    postop: "",
    intraop: "",
    wish: "",
  });
  const dragCounters = useRef<Record<TSurgeryStageType, number>>({
    preop: 0,
    markings: 0,
    postop: 0,
    intraop: 0,
    wish: 0,
  });

  const setError = (cat: TSurgeryStageType, msg: string) =>
    setErrors((prev) => ({ ...prev, [cat]: msg }));
  const clearError = (cat: TSurgeryStageType) =>
    setErrors((prev) => ({ ...prev, [cat]: "" }));

  /* ---------------- validation ----------------------------------- */

  const validateFile = async (
    file: File,
    category: TSurgeryStageType,
  ): Promise<boolean> => {
    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      setError(category, "Only PNG and JPG/JPEG files are allowed.");
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError(category, "File size should not exceed 50 MB.");
      return false;
    }
    const existing = (uploadState[category as keyof ImageUploadState] ??
      []) as UploadedImage[];
    const duplicate = existing.some((img) => img.file.name === file.name);
    if (duplicate) {
      setError(category, `File "${file.name}" has already been selected.`);
      return false;
    }

    clearError(category);
    return true;
  };

  /* ---------------- drag handlers -------------------------------- */

  const handleDragOver = (e: DragEvent, category: TSurgeryStageType) => {
    e.preventDefault();
    e.stopPropagation();
    // Ensure we're showing the copy cursor
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const handleDragEnter = (e: DragEvent, category: TSurgeryStageType): void => {
    e.preventDefault();
    e.stopPropagation();
    if (++dragCounters.current[category] === 1) {
      setDragStates((prev) => ({ ...prev, [category]: true }));
    }
  };

  const handleDragLeave = (e: DragEvent, category: TSurgeryStageType): void => {
    e.preventDefault();
    e.stopPropagation();
    if (--dragCounters.current[category] === 0) {
      setDragStates((prev) => ({ ...prev, [category]: false }));
    }
  };

  const handleDrop = async (
    e: DragEvent,
    category: TSurgeryStageType,
  ): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    dragCounters.current[category] = 0;
    setDragStates((prev) => ({ ...prev, [category]: false }));

    const files = Array.from(e.dataTransfer?.files || []);
    const valid: File[] = [];
    for (const f of files) if (await validateFile(f, category)) valid.push(f);
    if (valid.length) uploadManager.handleFilesDrop(category, valid);
  };

  /* ---------------- UI helpers ----------------------------------- */

  const renderUploadBox = (
    title: string,
    images: UploadedImage[],
    category: TSurgeryStageType,
  ) => {
    // Create refs for the DOM elements
    const uploadBoxRef = useRef<HTMLDivElement>(null);
    const dropZoneRef = useRef<HTMLDivElement>(null);

    // Set up event listeners when component mounts
    React.useEffect(() => {
      const uploadBox = uploadBoxRef.current;
      const dropZone = dropZoneRef.current;

      if (!uploadBox || !dropZone) return;

      const dragEnterHandler = (e: DragEvent) => handleDragEnter(e, category);
      const dragLeaveHandler = (e: DragEvent) => handleDragLeave(e, category);
      const dragOverHandler = (e: DragEvent) => handleDragOver(e, category);
      const dropHandler = (e: DragEvent) => handleDrop(e, category);

      // Add event listeners to both elements
      [uploadBox, dropZone].forEach((element) => {
        element.addEventListener("dragenter", dragEnterHandler);
        element.addEventListener("dragleave", dragLeaveHandler);
        element.addEventListener("dragover", dragOverHandler);
        element.addEventListener("drop", dropHandler);
      });

      // Cleanup
      return () => {
        [uploadBox, dropZone].forEach((element) => {
          element.removeEventListener("dragenter", dragEnterHandler);
          element.removeEventListener("dragleave", dragLeaveHandler);
          element.removeEventListener("dragover", dragOverHandler);
          element.removeEventListener("drop", dropHandler);
        });
      };
    }, [category]);

    return (
      <div
        ref={uploadBoxRef}
        style={{
          flex: 1,
          minWidth: 200,
          padding: 15,
          borderRadius: 10,
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: dragStates[category] ? "#007AFF" : "#DDDEE0",
          backgroundColor: dragStates[category] ? "#F0F8FF" : "#FAFAFA",
          boxShadow: dragStates[category]
            ? "0 0 10px rgba(0, 122, 255, 0.3)"
            : "none",
          transform: dragStates[category] ? "scale(1.02)" : "scale(1)",
          transition: "all 0.2s ease",
        }}
      >
        <Text style={[global.text, global.fontWeight600, { marginBottom: 10 }]}>
          {title}
        </Text>

        {!!errors[category] && (
          <Text
            style={[
              global.text,
              { color: "red", fontSize: 12, marginBottom: 8, flexWrap: "wrap" },
            ]}
          >
            {errors[category]}
          </Text>
        )}

        <div
          ref={dropZoneRef}
          style={{
            height: 120,
            borderRadius: 8,
            borderWidth: 2,
            borderStyle: "dashed",
            borderColor: dragStates[category] ? "#007AFF" : "#DDDEE0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: dragStates[category] ? "#F0F8FF" : "white",
            gap: 10,
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
          onClick={() => uploadManager.handleFileSelection(category)}
        >
          <Text style={[global.text, { textAlign: "center", color: "#666" }]}>
            {images.length
              ? `${images.length} image${images.length > 1 ? "s" : ""} selected`
              : dragStates[category]
                ? "Drop images here"
                : "drag & drop"}
          </Text>
          <Plus />
        </div>

        {images.length > 0 && (
          <ScrollView horizontal style={{ marginTop: 10 }}>
            {images.map((img, i) => (
              <View key={i} style={styles.imagePreview}>
                <img
                  src={URL.createObjectURL(img.file)}
                  alt={img.file.name}
                  style={{
                    width: 64,
                    height: 64,
                    objectFit: "cover",
                    borderRadius: 4,
                    marginBottom: 4,
                  }}
                />
                <Text style={[global.text, { fontSize: 10 }]} numberOfLines={1}>
                  {img.file.name.length > 12
                    ? `${img.file.name.substring(0, 12)}...`
                    : img.file.name}
                </Text>
                <Pressable
                  onPress={() => uploadManager.removeImage(category, i)}
                >
                  <Text style={[global.text, { color: "red", fontSize: 10 }]}>
                    Remove
                  </Text>
                </Pressable>
              </View>
            ))}
          </ScrollView>
        )}
      </div>
    );
  };

  /* ---------------- render --------------------------------------- */

  return (
    <View style={[styles.container, containerStyle]}>
      <NormalText
        text="Upload Patient Images"
        // fontWeight="700"
        style={{ fontSize: 18, marginBottom: 20 }}
      />

      <div
        style={{
          display: "flex",
          gap: 20,
          justifyContent: "space-around",
          flexDirection: isMobile ? "column" : "row",
          flexWrap: "wrap",
        }}
      >
        {renderUploadBox(IMAGE_CATEGORIES.preop, uploadState.preop, "preop")}
        {renderUploadBox(
          IMAGE_CATEGORIES.markings,
          uploadState.markings,
          "markings",
        )}
        {renderUploadBox(IMAGE_CATEGORIES.postop, uploadState.postop, "postop")}
        {renderUploadBox(IMAGE_CATEGORIES.wish, uploadState.wish, "wish")}
      </div>

      {showUploadButton && uploadManager.hasImagesToUpload() && (
        <PrimaryButton
          text={uploadState.isUploading ? "Uploading…" : "Upload"}
          roundBorderRadius
          icon={<Upload />}
          width={200}
          onPress={onUpload}
          disabled={uploadState.isUploading}
          style={{ marginTop: 20, alignSelf: "center" }}
        />
      )}
    </View>
  );
};

/* ------------------------------------------------------------------ */
/*  Styles                                                            */
/* ------------------------------------------------------------------ */

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20 },

  imagePreview: {
    width: 80,
    padding: 8,
    marginRight: 8,
    borderRadius: 6,
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#DDDEE0",
    alignItems: "center",
    minHeight: 100,
  },
});

export default ImageUploadComponent;
